/**
 * Centralised TanStack Query keys. Build every key from here so cache
 * invalidation stays refactor-safe.
 *
 * There is NO tenant scope segment: multi-tenant isolation is enforced entirely
 * server-side via the bearer token (a tenant_admin only ever sees its own
 * resources, a super_admin sees all), and no index accepts a tenantId filter.
 */
type Params = Record<string, unknown>

export const queryKeys = {
  session: ['session'] as const,

  properties: {
    all: ['properties'] as const,
    list: (params?: Params) => ['properties', 'list', params ?? {}] as const,
    detail: (id: string) => ['properties', 'detail', id] as const,
  },

  affiliatePartners: {
    all: ['affiliatePartners'] as const,
    list: (params?: Params) => ['affiliatePartners', 'list', params ?? {}] as const,
    detail: (id: string) => ['affiliatePartners', 'detail', id] as const,
  },

  affiliateLinks: {
    all: ['affiliateLinks'] as const,
    list: (params?: Params) => ['affiliateLinks', 'list', params ?? {}] as const,
    detail: (id: string) => ['affiliateLinks', 'detail', id] as const,
  },

  auditLogs: {
    all: ['auditLogs'] as const,
    list: (params?: Params) => ['auditLogs', 'list', params ?? {}] as const,
    detail: (id: string) => ['auditLogs', 'detail', id] as const,
  },

  conversions: {
    all: ['conversions'] as const,
    list: (params?: Params) => ['conversions', 'list', params ?? {}] as const,
  },

  alerts: {
    all: ['alerts'] as const,
    list: (params?: Params) => ['alerts', 'list', params ?? {}] as const,
    summary: ['alerts', 'summary'] as const,
  },

  clicks: {
    all: ['clicks'] as const,
    list: (since?: string, propertyId?: string) =>
      ['clicks', 'list', since ?? null, propertyId ?? null] as const,
    timeseries: (since?: string, interval?: string, propertyId?: string, targetKey?: string) =>
      [
        'clicks',
        'timeseries',
        since ?? null,
        interval ?? null,
        propertyId ?? null,
        targetKey ?? null,
      ] as const,
    gaps: (since?: string, propertyId?: string) =>
      ['clicks', 'gaps', since ?? null, propertyId ?? null] as const,
  },
} as const
